"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TemplateLoader = void 0;
const libs_i18n_core_1 = require("libs.i18n-core");
const commonjs_bundler_1 = require("./bundler/commonjs_bundler");
const function_module_1 = require("./bundler/function_module");
const function_renderer_1 = require("./renderers/function_renderer");
const text_renderer_1 = require("./renderers/text_renderer");
const ES_MODULE_INTEROP = 'Object.defineProperty(exports, "__esModule", { value: true });';
let allManifests;
let cachedStrings = {};
let cachedResources = {};
let wasTemplateRead = {};
/** Template loaders tha dynamically laod a template. */
/* eslint @typescript-eslint/no-explicit-any:0 */
class TemplateLoader {
    constructor(templateLibrary, translationProvider) {
        this.templateLibrary = templateLibrary;
        this.translationProvider = translationProvider;
    }
    /** Loads a template given its ID/version in the format: id~version */
    loadTemplate(templateId_1) {
        return __awaiter(this, arguments, void 0, function* (templateId, locale = libs_i18n_core_1.Locale.en_US, localeVersion) {
            allManifests = yield this.templateLibrary.loadAllManifests();
            const templateRec = yield this.loadTemplateRec(templateId, locale, {
                // Built-in modules to support i18n
                'i18n.language': { default: locale, __esModule: true },
                'i18n.rtl': { default: (0, libs_i18n_core_1.isRtl)(locale), __esModule: true },
            }, localeVersion);
            // reset cache
            // Resetting the cache here causes a race condition when multiple calls to loadTemplate are made in parallel because
            // one call will complete its recursive flow and blow away the cache while another call is still expecting the cache
            // to have been fully populated.
            // TODO: Investigate if it makes sense to populate the cache once only per TemplateLoader instance and not reset it.
            // It might even make sense to put a caching layer in the TemplateLibrary or firebase implementation of the storage
            // api instead.
            // allManifests = {};
            cachedStrings = {};
            cachedResources = {};
            wasTemplateRead = {};
            return templateRec;
        });
    }
    /** Filters the global manifests object and returns the required manifest */
    filterManifests(id) {
        return __awaiter(this, void 0, void 0, function* () {
            const manifest = allManifests[id];
            if (!manifest) {
                return yield this.templateLibrary.getManifest(id);
            }
            return manifest;
        });
    }
    findStringIdsWithinManifest(manifest) {
        // Check if the current object has the key 'strings'
        if ('strings' in manifest) {
            return manifest.strings;
        }
        // If the current object does not have 'strings', recursively search its children
        for (const key in manifest) {
            if (typeof manifest[key] === 'object') {
                const result = this.findStringIdsWithinManifest(manifest[key]);
                if (result !== null) {
                    return result;
                }
            }
        }
        // If 'strings' key is not found at any depth, return null
        return null;
    }
    /** Loads a template, caching previously loaded templates in the same call */
    loadTemplateRec(templateId, locale, loadedTemplates, localeVersion) {
        return __awaiter(this, void 0, void 0, function* () {
            const loadedTemplate = loadedTemplates[templateId];
            if (loadedTemplate !== undefined) {
                return loadedTemplate;
            }
            const manifest = yield this.filterManifests(templateId);
            const resourceLoader = this.templateLibrary.getResourceLoader(templateId);
            const [loadedResources, loadedStrings] = yield Promise.all([
                resourceLoader.loadAll(),
                this.translationProvider.loadStringsByIds(Object.keys(this.findStringIdsWithinManifest(manifest) || {}), localeVersion),
            ]);
            try {
                const template = yield this.loadTemplateFromManifest(locale, manifest, resourceLoader, loadedTemplates, loadedResources, loadedStrings, localeVersion);
                loadedTemplates[templateId] = template;
                return template;
            }
            catch (error) {
                console.error(`Failed to load template ${templateId}.`);
                throw error;
            }
        });
    }
    /** Loads a template from a manifest */
    loadTemplateFromManifest(locale, manifest, resourceLoader, loadedTemplates, loadedResources, loadedStrings, localeVersion) {
        return __awaiter(this, void 0, void 0, function* () {
            switch (manifest.type) {
                case "function" /* TemplateType.FUNCTION */:
                    return yield this.loadFunctionTemplate(locale, resourceLoader, manifest, loadedTemplates, loadedResources, loadedStrings, localeVersion);
                case "json" /* TemplateType.JSON */:
                    throw new Error('JSON templates need to be pre-compiled');
                case "text" /* TemplateType.TEXT */:
                    return yield this.loadTextTemplate(manifest, loadedResources);
                case "library" /* TemplateType.LIBRARY */:
                    return yield this.loadLibraryTemplate(locale, resourceLoader, manifest, loadedTemplates, loadedResources, loadedStrings, localeVersion);
            }
        });
    }
    /** Loader for a function template */
    loadFunctionTemplate(locale_1, resourceLoader_1, _a, loadedTemplates_1, loadedResources_1, loadedStrings_1, localeVersion_1) {
        return __awaiter(this, arguments, void 0, function* (locale, resourceLoader, { dependencies, main }, loadedTemplates, loadedResources, loadedStrings, localeVersion) {
            const module = loadedResources[main];
            const loadedDependencies = yield this.loadDependencies(locale, resourceLoader, dependencies, loadedTemplates, loadedResources, loadedStrings, localeVersion);
            return (0, function_renderer_1.loadFunctionRenderer)(module, loadedDependencies);
        });
    }
    /** Loader for a Text template */
    loadTextTemplate(_a, loadedResources_1) {
        return __awaiter(this, arguments, void 0, function* ({ text_template }, loadedResources) {
            return text_renderer_1.TextRenderer.compile(loadedResources[text_template]);
        });
    }
    /** Loader for a library template */
    loadLibraryTemplate(locale_1, resourceLoader_1, _a, loadedTemplates_1, loadedResources_1, loadedStrings_1, localeVersion_1) {
        return __awaiter(this, arguments, void 0, function* (locale, resourceLoader, { libraries, dependencies }, loadedTemplates, loadedResources, loadedStrings, localeVersion) {
            const libraryModules = yield this.loadDependencies(locale, resourceLoader, dependencies, loadedTemplates, loadedResources, loadedStrings, localeVersion);
            for (let i = 0; i < libraries.length; i++) {
                const { module, path } = libraries[i];
                // Load each library module in order, since on module might dependent on the previous one
                libraryModules[module] = (0, function_renderer_1.loadFunctionRenderer)(loadedResources[path], libraryModules);
            }
            return { default: () => libraryModules, library: true, __esModule: true };
        });
    }
    /** Recursively load all the dependencies */
    loadDependencies(locale_1, resourceLoader_1) {
        return __awaiter(this, arguments, void 0, function* (locale, resourceLoader, dependencies = {}, loadedTemplates, loadedResources, loadedStrings, localeVersion) {
            const modules = {};
            yield Promise.all([
                this.loadTemplateDependencies(locale, modules, dependencies.templates, loadedTemplates, localeVersion),
                this.loadSubTemplateDependencies(locale, modules, dependencies.sub_templates, loadedTemplates, resourceLoader, loadedResources, loadedStrings, localeVersion),
                this.loadBundlesDependencies(locale, modules, dependencies.bundles, localeVersion),
                this.loadSubBundlesDependencies(locale, modules, dependencies.sub_bundles, resourceLoader, loadedResources, loadedStrings, localeVersion),
                this.loadLibrariesDependencies(locale, modules, dependencies.libraries, loadedTemplates, localeVersion),
                this.loadStringsDependencies(locale, modules, dependencies.strings, loadedStrings, localeVersion),
                this.loadAssetsDependencies(modules, dependencies.assets, resourceLoader, loadedResources),
            ]);
            return modules;
        });
    }
    /** Loads templates dependencies */
    loadTemplateDependencies(locale_1, modules_1) {
        return __awaiter(this, arguments, void 0, function* (locale, modules, dependencies = [], loadedTemplates, localeVersion) {
            return Promise.all(dependencies.map((dependency) => __awaiter(this, void 0, void 0, function* () {
                modules[dependency] = yield this.loadTemplateRec(dependency, locale, loadedTemplates, localeVersion);
            })));
        });
    }
    /** Loads sub-templates dependencies */
    loadSubTemplateDependencies(locale_1, modules_1) {
        return __awaiter(this, arguments, void 0, function* (locale, modules, dependencies = {}, loadedTemplates, resourceLoader, loadedResources, loadedStrings, localeVersion) {
            return Promise.all(Object.entries(dependencies).map((_a) => __awaiter(this, [_a], void 0, function* ([localId, manifest]) {
                modules[localId] = yield this.loadTemplateFromManifest(locale, manifest, resourceLoader, loadedTemplates, loadedResources, loadedStrings, localeVersion);
            })));
        });
    }
    /** Loads templates dependencies as bundles */
    loadBundlesDependencies(locale_1, modules_1) {
        return __awaiter(this, arguments, void 0, function* (locale, modules, dependencies = [], localeVersion) {
            return Promise.all(dependencies.map((dependency) => __awaiter(this, void 0, void 0, function* () {
                const bundleModules = {
                    // Built-in modules to support i18n
                    'i18n.language': `exports.default = '${locale}';`,
                    'i18n.rtl': `exports.default = ${(0, libs_i18n_core_1.isRtl)(locale)};`,
                };
                const assets = {};
                yield this.collectModules(locale, dependency, bundleModules, assets, localeVersion);
                const bundle = new commonjs_bundler_1.CommonJsBundler().bundle(bundleModules);
                modules[dependency] = { default: () => ({ bundle, assets }), __esModule: true };
            })));
        });
    }
    /** Loads sub-templates dependencies as bundles */
    loadSubBundlesDependencies(locale_1, modules_1) {
        return __awaiter(this, arguments, void 0, function* (locale, modules, dependencies = {}, resourceLoader, loadedResources, loadedStrings, localeVersion) {
            return Promise.all(Object.entries(dependencies).map((_a) => __awaiter(this, [_a], void 0, function* ([localId, manifest]) {
                const bundleModules = {};
                const assets = {};
                yield this.collectModulesFromManifest(locale, localId, manifest, resourceLoader, bundleModules, assets, loadedResources, loadedStrings, localeVersion);
                const bundle = new commonjs_bundler_1.CommonJsBundler().bundle(bundleModules);
                modules[localId] = { default: () => ({ bundle, assets }), __esModule: true };
            })));
        });
    }
    /** Loads library dependencies */
    loadLibrariesDependencies(locale_1, modules_1) {
        return __awaiter(this, arguments, void 0, function* (locale, modules, dependencies = [], loadedTemplates, localeVersion) {
            return Promise.all(dependencies.map((dependency) => __awaiter(this, void 0, void 0, function* () {
                const libraryTemplate = yield this.loadTemplateRec(dependency, locale, loadedTemplates, localeVersion);
                if (!libraryTemplate.library) {
                    throw new Error(`Template ${dependency} is loaded as a library while it is not a library.`);
                }
                const libraryDefinitions = libraryTemplate.default();
                Object.entries(libraryDefinitions).forEach(([name, module]) => (modules[name] = module));
            })));
        });
    }
    /** Loads assets dependencies */
    loadAssetsDependencies(modules_1) {
        return __awaiter(this, arguments, void 0, function* (modules, { text = [], binary = [], json = [] } = {}, resourceLoader, loadedResources) {
            return Promise.all([
                ...text.map((path) => __awaiter(this, void 0, void 0, function* () {
                    const asset = loadedResources[path];
                    modules[`./${path}`] = { default: asset, __esModule: true };
                    modules[`./${path}?url`] = { default: `assets/${path}`, __esModule: true };
                })),
                ...json.map((path) => __awaiter(this, void 0, void 0, function* () {
                    const asset = JSON.parse(loadedResources[path]);
                    modules[`./${path}`] = { default: asset, __esModule: true };
                    modules[`./${path}?url`] = { default: `assets/${path}`, __esModule: true };
                })),
                ...binary.map((path) => __awaiter(this, void 0, void 0, function* () {
                    const asset = new Uint8Array(yield resourceLoader.loadBinary(path));
                    modules[`./${path}`] = { default: asset, __esModule: true };
                    modules[`./${path}?url`] = { default: `assets/${path}`, __esModule: true };
                })),
            ]);
        });
    }
    /** Loads strings dependencies */
    loadStringsDependencies(locale_1, modules_1) {
        return __awaiter(this, arguments, void 0, function* (locale, modules, dependencies = {}, loadedStrings, localeVersion) {
            if (!loadedStrings) {
                return;
            }
            return Promise.all(Object.entries(dependencies).map((_a) => __awaiter(this, [_a], void 0, function* ([stringId]) {
                const stringFunction = yield this.translationProvider.getStringAsFunction(stringId, locale, loadedStrings[stringId], localeVersion);
                modules[stringId] = { default: stringFunction, __esModule: true };
            })));
        });
    }
    generateTemplateResourcesAndStrings(resourceLoader, manifest, localeVersion) {
        return Promise.all([
            resourceLoader.loadAll(),
            this.translationProvider.loadStringsByIds(Object.keys(this.findStringIdsWithinManifest(manifest) || {}), localeVersion),
        ]);
    }
    /** Collects recursively all modules used by a template. */
    collectModules(locale, templateId, modules, assets, localeVersion) {
        return __awaiter(this, void 0, void 0, function* () {
            // This template is already bundled
            if (modules[templateId] !== undefined) {
                return;
            }
            const manifest = yield this.filterManifests(templateId);
            const resourceLoader = this.templateLibrary.getResourceLoader(templateId);
            // const [loadedResources, loadedStrings] = wasTemplateRead[templateId] ? [cachedResources[templateId], cachedStrings[templateId]] : await this.generateTemplateResourcesAndStrings(resourceLoader, manifest);
            let loadedResources;
            let loadedStrings;
            if (wasTemplateRead && wasTemplateRead[templateId]) {
                [loadedResources, loadedStrings] = [cachedResources[templateId], cachedStrings[templateId]];
            }
            else {
                [loadedResources, loadedStrings] = yield this.generateTemplateResourcesAndStrings(resourceLoader, manifest);
            }
            wasTemplateRead[templateId] = true;
            cachedResources[templateId] = loadedResources;
            cachedStrings[templateId] = loadedStrings;
            return this.collectModulesFromManifest(locale, templateId, manifest, resourceLoader, modules, assets, loadedResources, loadedStrings, localeVersion);
        });
    }
    /** Collects recursively all modules used by a template from its manifest. */
    collectModulesFromManifest(locale, templateId, manifest, resourceLoader, modules, assets, loadedResources, loadedStrings, localeVersion) {
        return __awaiter(this, void 0, void 0, function* () {
            switch (manifest.type) {
                case "function" /* TemplateType.FUNCTION */:
                    yield this.collectDependencies(locale, manifest.dependencies, resourceLoader, modules, assets, loadedResources, loadedStrings, localeVersion);
                    modules[templateId] = new function_module_1.FunctionModule().generateModule(loadedResources[manifest.main]);
                    break;
                case "json" /* TemplateType.JSON */: {
                    throw new Error('JSON templates need to be pre-compiled');
                }
                case "library" /* TemplateType.LIBRARY */: {
                    const { libraries } = manifest;
                    for (let i = 0; i < libraries.length; i++) {
                        const { module, path } = libraries[i];
                        modules[module] = loadedResources[path];
                    }
                    break;
                }
                case "text" /* TemplateType.TEXT */:
                    throw new Error('Text templates in bundles not supported yet');
            }
        });
    }
    /** Collects recursively all dependent modules. */
    collectDependencies(locale_1) {
        return __awaiter(this, arguments, void 0, function* (locale, dependencies = {}, resourceLoader, modules, assets, loadedResources, loadedStrings, localeVersion) {
            const { templates = [], libraries = [], assets: localAssets = {}, bundles = [], sub_templates = {}, sub_bundles = {}, } = dependencies;
            if (Object.keys(sub_templates).length !== 0) {
                throw new Error('Sub-templates inside a bundle are not supported');
            }
            if (bundles.length !== 0) {
                throw new Error('Bundles inside a bundle are not supported');
            }
            if (Object.keys(sub_bundles).length !== 0) {
                throw new Error('Bundles inside a bundle are not supported');
            }
            yield Promise.all([
                this.collectTemplatesDependencies(locale, templates, modules, assets, localeVersion),
                this.collectLibrariesDependencies(locale, libraries, modules, assets, localeVersion),
                this.collectAssetsDependencies(localAssets, resourceLoader, modules, assets, loadedResources),
                this.collectStringsDependencies(locale, dependencies.strings, modules, loadedStrings, localeVersion),
            ]);
        });
    }
    /** Collects dependent templates modules. */
    collectTemplatesDependencies(locale_1) {
        return __awaiter(this, arguments, void 0, function* (locale, dependencies = [], modules, assets, localeVersion) {
            for (let i = 0; i < dependencies.length; i++) {
                const dependency = dependencies[i];
                if (modules[dependency]) {
                    continue;
                }
                yield this.collectModules(locale, dependency, modules, assets, localeVersion);
            }
        });
    }
    /** Collects dependent libraries. */
    collectLibrariesDependencies(locale_1) {
        return __awaiter(this, arguments, void 0, function* (locale, dependencies = [], modules, assets, localeVersion) {
            for (let i = 0; i < dependencies.length; i++) {
                const dependency = dependencies[i];
                if (modules[dependency]) {
                    continue;
                }
                yield this.collectModules(locale, dependency, modules, assets, localeVersion);
            }
        });
    }
    /** Collects assets. */
    collectAssetsDependencies() {
        return __awaiter(this, arguments, void 0, function* ({ text = [], binary = [], json = [] } = {}, resourceLoader, modules, assets, loadedResources) {
            return Promise.all([
                ...[...text, ...json].map((path) => __awaiter(this, void 0, void 0, function* () {
                    const assetPath = `assets/${path}`;
                    modules[`./${path}?url`] = ES_MODULE_INTEROP + `exports.default = '${assetPath}';\n`;
                    assets[assetPath] = loadedResources[path];
                })),
                ...binary.map((path) => __awaiter(this, void 0, void 0, function* () {
                    const assetPath = `assets/${path}`;
                    modules[`./${path}?url`] = ES_MODULE_INTEROP + `exports.default = '${assetPath}';\n`;
                    assets[assetPath] = new Uint8Array(yield resourceLoader.loadBinary(path));
                })),
            ]);
        });
    }
    /** Collects translated strings. */
    collectStringsDependencies(locale_1) {
        return __awaiter(this, arguments, void 0, function* (locale, dependencies = {}, bundle, loadedStrings, localeVersion) {
            if (!loadedStrings) {
                return;
            }
            return Promise.all(Object.entries(dependencies).map((_a) => __awaiter(this, [_a], void 0, function* ([stringId]) {
                bundle[stringId] =
                    ES_MODULE_INTEROP +
                        `exports.default = ${yield this.translationProvider.getStringAsCode(stringId, locale, loadedStrings[stringId], localeVersion)};\n`;
            })));
        });
    }
}
exports.TemplateLoader = TemplateLoader;
//# sourceMappingURL=template_loader.js.map